import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";
import { CandidateExamService } from "../../service/assessSections/CandidateExamService";
import { isExamModeActive } from "../../pages/assessSections/ExamMode";
import { testService } from "../../service/candidate/testService";
import { gradeService } from "../../service/candidate/gradeService";
import { candidateService } from "../../service/settings/candidate/candidateService";
import { examService } from "../../service/candidate/examService";
import { AssessmentService } from "../../service/candidate/assessmentService";
import { AssessExamService } from "../../service/assessment/assessExamService";
import { history } from "../../helper/history";
import { routes } from "../../helper/routes";
import { sweetSuccessAlert } from "../../components/common/SweetAlert";

const ExamContext = createContext(null);

const defaultQuery = {
    limit: 10,
    offset: 0,
    search_term: "",
    sort_column: "created_at",
    sort_direction: "desc",
    selectedCategories: "null",
    selectedAuthors: "null",
    selectedChapters: "null",
    selectedGrades: "null",
    selectedDifficulties: "null",
    selectedLanguages: "null",
    selectedTerms: "null",
    selectedYears: "null",
    selectedsubjects: "null",
    selectedExamCategories: "null",
    selectedSources: "null",
    selectedCreators: "null",
    selectedStartDate: "null",
    selectedEndDate: "null",
};

function normalizeMetaData(metaData = []) {
    const result = {};

    metaData.forEach((group) => {
        const key = Object.keys(group)[0];
        result[key] = group[key] || [];
    });

    return result;
}

function parseExamQuestions(examQuestionsString) {
    try {
        const parsed = JSON.parse(examQuestionsString);
        return parsed?.questions || [];
    } catch (error) {
        return [];
    }
}

const QUESTION_TYPES = {
    MCQ: "MCQ",
    SUBQUESTION: "Sub Question",
};

function getGradableQuestions(questions = []) {
    return questions.filter((question) => {
        const marks = Number(question.mark_allocation || 0);

        if (question.is_subquestion === "true") {
            return marks > 0;
        }

        if (question.question_type !== QUESTION_TYPES.SUBQUESTION) {
            return marks > 0;
        }

        return false;
    });
}

function isMcqOnlyPaper(questions = []) {
    const gradableQuestions = getGradableQuestions(questions);

    return (
        gradableQuestions.length > 0 &&
        gradableQuestions.every(
            (question) => question.question_type === QUESTION_TYPES.MCQ
        )
    );
}

function getTotalMarkAllocation(questions = []) {
    return getGradableQuestions(questions).reduce(
        (total, question) => total + Number(question.mark_allocation || 0),
        0
    );
}

function parseAnswerContent(value) {
    try {
        return typeof value === "string" ? JSON.parse(value) : value;
    } catch {
        return [];
    }
}

function buildCandidateAnswerMap(candidateAnswers = []) {
    return candidateAnswers.reduce((map, answer) => {
        map[Number(answer.question_id)] = answer;
        return map;
    }, {});
}

function calculateMcqMarks({ question, candidateAnswer, answerContent }) {
    const selectedAnswers = parseAnswerContent(candidateAnswer?.answer_content) || [];

    if (!Array.isArray(selectedAnswers)) {
        return 0;
    }

    const answerPercentages = String(answerContent || "")
        .split(",")
        .map((item) => Number(item));

    const numberOfCorrectAnswers = answerPercentages.filter(
        (item) => item && item !== 0 && !Number.isNaN(item)
    ).length;

    if (selectedAnswers.length > numberOfCorrectAnswers) {
        return 0;
    }

    return selectedAnswers.reduce((total, answerIndex) => {
        const percentage = Number(answerPercentages[answerIndex] || 0);

        return (
            total +
            (percentage * Number(question.mark_allocation || 0)) / 100
        );
    }, 0);
}

function getGradeScale(scales = [], percentage) {
    return scales.find(
        (scale) =>
            Number(percentage) >= Number(scale.min_marks) &&
            Number(percentage) <= Number(scale.max_marks)
    );
}

function buildRemarkRawText(text) {
    return {
        blocks: [
            {
                key: "auto",
                text: text || "Auto graded MCQ questions.",
                type: "unstyled",
                depth: 0,
                inlineStyleRanges: [],
                entityRanges: [],
                data: {},
            },
        ],
        entityMap: {},
    };
}

function getExamStartTimeFromData(timerData, exam) {
    return (
        timerData?.attempt_start_time ||
        timerData?.start_time ||
        timerData?.data?.attempt_start_time ||
        timerData?.data?.start_time ||
        exam?.attempt_start_time ||
        exam?.start_time ||
        null
    );
}


function parseBackendDateTime(value) {
    if (!value) return null;

    const text = String(value).trim();

    if (text.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(text)) {
        return new Date(text);
    }

    return new Date(`${text}Z`);
}

function hasExamTimeExpired({ exam, timerData }) {
    if (!exam?.is_set_time_limit) return false;

    const interval = Number(exam?.exam_time_interval || 0);
    if (!interval) return false;

    const unitId = Number(exam?.exam_time_interval_unit_id || 1);

    let durationMs = interval * 60 * 1000; // minutes default

    if (unitId === 2) {
        durationMs = interval * 60 * 60 * 1000; // hours
    } else if (unitId === 3) {
        durationMs = interval * 24 * 60 * 60 * 1000; // days
    }

    const startTime = getExamStartTimeFromData(timerData, exam);
    if (!startTime) return false;

    const parsedStartDate = parseBackendDateTime(startTime);
    const startMs = parsedStartDate?.getTime();
    if (!startMs || Number.isNaN(startMs)) return false;

    const endMs = startMs + durationMs;

    return Date.now() >= endMs;
}

const getCandidateStatusForView = (viewMode) => {
    if (viewMode === "grade-reports") return "Finished";
    return "Assigned || Onprogress";
};

export function ExamProvider({ children }) {
    const [metaData, setMetaData] = useState({});
    const [metaLoading, setMetaLoading] = useState(false);
    const [hasLoadedExams, setHasLoadedExams] = useState(false);
    const [examPaper, setExamPaper] = useState(null);
    const [examQuestions, setExamQuestions] = useState([]);
    const [examSections, setExamSections] = useState([]);
    const [examLoading, setExamLoading] = useState(false);
    const [candidateAnswers, setCandidateAnswers] = useState([]);
    const [examStartData, setExamStartData] = useState(null);
    const [items, setItems] = useState([]);
    const [allAttemptItems, setAllAttemptItems] = useState([]);
    const [query, setQuery] = useState(defaultQuery);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [restartPauseStatus, setRestartPauseStatus] = useState(null);
    const [flow, setFlow] = useState({
        view: "list",
        selectedExam: null,
        mode: null,
    });
    const [examViewMode, setExamViewMode] = useState("new-exams");

    const saveAnswer = useCallback(async ({
        question_id,
        answer_content,
    }) => {
        try {
            const selectedExam = flow?.selectedExam || examPaper;

            if (!selectedExam?.candidate_id) {
                console.error("Missing candidate_id. Cannot save answer.", {
                    selectedExam,
                    examPaper,
                    flow,
                });
                return;
            }

            const payload = [
                {
                    assessment_id: selectedExam.assessment_id || selectedExam.id,
                    candidate_id: selectedExam.candidate_id,
                    question_id,
                    answer_content,
                    answer_state: "Not_Graded",
                    attempt_no: selectedExam.attempt_no,
                    is_answer_content_blob: "false",
                },
            ];

            console.log("Saving answer payload:", payload);

            return await CandidateExamService.saveCandidateAnswers(payload);
        } catch (error) {
            console.error("Failed to save answer", error);
        }
    }, [flow, examPaper]);

    const saveAnswersBulk = useCallback(async (answers = {}, examOverride = null) => {
        const selectedExam = examOverride || flow?.selectedExam || examPaper;
        if (!selectedExam?.candidate_id) {
            console.error("Missing candidate_id. Cannot save answers.", {
                selectedExam,
                examPaper,
                flow,
            });
            return null;
        }

        const answerEntries = Object.entries(answers);

        if (!answerEntries.length) return null;

        const payload = answerEntries.map(([questionId, answerValue]) => ({
            assessment_id: selectedExam.assessment_id || selectedExam.id,
            candidate_id: selectedExam.candidate_id,
            question_id: Number(questionId),
            answer_content: JSON.stringify(answerValue),
            answer_state: "Not_Graded",
            attempt_no: selectedExam.attempt_no,
            is_answer_content_blob: "false",
        }));

        console.log("Bulk saving answers:", payload);

        return CandidateExamService.saveCandidateAnswers(payload);
    }, [flow, examPaper]);

    const autoGradeMcqPaper = useCallback(async (selectedExam) => {
        const assessmentId = selectedExam.assessment_id || selectedExam.id;
        const candidateId = selectedExam.candidate_id;
        const attemptNo = selectedExam.attempt_no || 1;

        if (!assessmentId || !candidateId) {
            throw new Error("Missing assessment or candidate details for autograde.");
        }

        const paperResponse = await CandidateExamService.loadAssessmentQuestions(assessmentId);

        const paperPayload =
            paperResponse?.data?.data ||
            paperResponse?.data ||
            {};

        const questions = paperPayload.examQuestions
            ? parseExamQuestions(paperPayload.examQuestions)
            : paperPayload.questions || [];

        const isMcqOnly = isMcqOnlyPaper(questions);

        if (!isMcqOnly) {
            return null;
        }

        const answersResponse = await CandidateExamService.getCandidateAnswers(candidateId);

        const candidateAnswers =
            answersResponse?.data?.answers ||
            answersResponse?.data?.data?.answers ||
            [];

        const candidateAnswerMap = buildCandidateAnswerMap(candidateAnswers);

        const answerContentResponse = await testService.getAllAnswerContents({
            questions,
        });

        const answerContents =
            answerContentResponse?.data?.data ||
            answerContentResponse?.data ||
            answerContentResponse ||
            [];

        const questionsForSaveMarks = candidateAnswers.map((answer) => {
            const question = questions.find(
                (item) => Number(item.question_id) === Number(answer.question_id)
            );

            if (!question || question.question_type !== QUESTION_TYPES.MCQ) {
                return answer;
            }

            const questionIndex = questions.findIndex(
                (item) => Number(item.question_id) === Number(question.question_id)
            );

            const marks = calculateMcqMarks({
                question,
                candidateAnswer: candidateAnswerMap[question.question_id],
                answerContent: answerContents[questionIndex],
            });

            return {
                ...answer,
                mark_obtained: Number(marks),
                graded_by: "root",
            };
        });

        const totalAutoMarks = questionsForSaveMarks.reduce(
            (sum, answer) => sum + Number(answer.mark_obtained || 0),
            0
        );

        const totalMarkAllocation = Number(getTotalMarkAllocation(questions) || 0);

        const gradeScaleResponse = await candidateService.getAllGradeScales();

        const gradeScales =
            gradeScaleResponse?.data?.candidate_grade_scales ||
            gradeScaleResponse?.data?.data?.candidate_grade_scales ||
            [];

        const percentage = totalMarkAllocation
            ? (totalAutoMarks / totalMarkAllocation) * 100
            : 0;

        const gradeScale = getGradeScale(gradeScales, percentage);

        const userRoleId = selectedExam.created_by
            ? `CASS${selectedExam.created_by}`
            : sessionStorage.getItem("user_role_id") || "root";

        await CandidateExamService.saveMarks({
            assessment_id: assessmentId,
            questions: questionsForSaveMarks,
            user_role_id: userRoleId,
            assessor_remarks: buildRemarkRawText(
                gradeScale?.remarks || "Auto graded MCQ questions."
            ),
            candidate_id: candidateId,
            attempt_no: attemptNo,
        });

        await CandidateExamService.finalizeAutoGrade({
            candidate_id: candidateId,
            final_grade: Number(totalAutoMarks),
            user_role_id: userRoleId,
            attempt_no: attemptNo,
            total_mark_allocation: totalMarkAllocation,
            grade: gradeScale?.grade || null,
        });

        await CandidateExamService.getReport({
            candidate_id: candidateId,
            attempt_no: attemptNo,
        });

        return {
            totalAutoMarks,
            totalMarkAllocation,
            grade: gradeScale?.grade || null,
        };
    }, []);

    // const loadPublishedExams = useCallback(async (nextQuery = query, isMounted = { current: true }) => {
    //     try {
    //         setLoading(true);
    //         setErrorMessage("");

    //         const response = await CandidateExamService.getPublishedExams(nextQuery);

    //         if (!isMounted.current) return;

    //         setItems(response?.data?.items || response?.data?.data?.items || []);
    //         setTotalCount(response?.data?.total_count || response?.data?.data?.total_count || 0);
    //         setHasLoadedExams(true);
    //     } catch (error) {
    //         if (!isMounted.current) return;
    //         setErrorMessage(error?.message || "Failed to load exams.");
    //         setItems([]);
    //         setTotalCount(0);
    //     } finally {
    //         if (isMounted.current) setLoading(false);
    //     }
    // }, [query]);

    const mapMyExamItem = (item) => {
        const status = item.candidate_status;

        return {
            ...item,
            id: item.assessment_id || item.id,
            name: item.assess_name || item.name || item.title || "Untitled Exam",
            time: item.exam_time_interval
                ? `${item.exam_time_interval} minutes`
                : "Unlimited",
            attempt: `${item.attempt_no || 1} out of ${item.no_of_attempts || 1}`,
            action:
                status === "Finished"
                    ? "grade-report"
                    : status === "Onprogress"
                        ? "resume"
                        : "start",
        };
    };

    const loadMyExams = useCallback(async (
        nextQuery = query,
        isMounted = { current: true },
        viewMode = examViewMode
    ) => {
        try {
            setLoading(true);
            setErrorMessage("");

            const loginUserId = sessionStorage.getItem("user_id");

            const fetchByStatus = (status) =>
                AssessExamService.getEnrolledNodeExamsWithoutNode(
                    loginUserId,
                    nextQuery.limit,
                    nextQuery.offset,
                    nextQuery.sort_column,
                    nextQuery.sort_direction,
                    nextQuery.search_term,
                    nextQuery.selectedGrades,
                    nextQuery.selectedChapters,
                    nextQuery.selectedsubjects,
                    nextQuery.selectedAuthors,
                    nextQuery.selectedYears,
                    nextQuery.selectedCategories,
                    nextQuery.selectedTerms,
                    nextQuery.selectedDifficulties,
                    nextQuery.selectedLanguages,
                    status,
                    "null"
                );

            const responses =
                viewMode === "grade-reports"
                    ? await Promise.all([
                        fetchByStatus("Finished"),
                    ])
                    : await Promise.all([
                        fetchByStatus("Assigned"),
                        fetchByStatus("Onprogress"),
                    ]);

            if (!isMounted.current) return;

            const allItems = responses.flatMap((response) => {
                const payload = response?.data?.data || response?.data || response || {};
                return Array.isArray(payload.enrolled_node_exams_list)
                    ? payload.enrolled_node_exams_list
                    : [];
            });

            const mappedAllItems = allItems.map(mapMyExamItem);

            const visibleItems =
                viewMode === "grade-reports"
                    ? mappedAllItems.filter((item) => item.candidate_status === "Finished")
                    : mappedAllItems;

            setItems(visibleItems);
            setAllAttemptItems(mappedAllItems);
            const backendTotalCount = responses.reduce((total, response) => {
                const payload = response?.data?.data || response?.data || response || {};
                return total + Number(payload.total_count || 0);
            }, 0);

            setTotalCount(backendTotalCount);
            setHasLoadedExams(true);


        } catch (error) {
            if (!isMounted.current) return;
            setErrorMessage(error?.message || "Failed to load my exams.");
            setItems([]);
            setTotalCount(0);
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, [query, examViewMode]);

    const finishExam = useCallback(async ({
        answers = {},
        is_all_mcq = null,
        examOverride = null,
        questionsOverride = null,
        stayOnPaper = false,
    } = {}) => {
        const selectedExam = examOverride || flow?.selectedExam || examPaper;

        if (!selectedExam?.candidate_id) {
            throw new Error("Missing candidate_id. Cannot finish exam.");
        }

        await saveAnswersBulk(answers, selectedExam);

        let shouldAutoGradeMcq = is_all_mcq;

        if (shouldAutoGradeMcq === null) {
            if (Array.isArray(questionsOverride) && questionsOverride.length > 0) {
                shouldAutoGradeMcq = isMcqOnlyPaper(questionsOverride);
            } else if (Array.isArray(examQuestions) && examQuestions.length > 0) {
                shouldAutoGradeMcq = isMcqOnlyPaper(examQuestions);
            } else {
                shouldAutoGradeMcq = false;
            }
        }

        if (shouldAutoGradeMcq) {
            await autoGradeMcqPaper(selectedExam);
        }

        const response = await CandidateExamService.finishTest({
            candidate_id: selectedExam.candidate_id,
            is_all_mcq: shouldAutoGradeMcq,
        });

        if (!response?.success) {
            throw new Error(
                response?.message?.error ||
                response?.message ||
                "Failed to finish exam."
            );
        }

        if (!stayOnPaper) {
            setFlow({
                view: "list",
                selectedExam: null,
                mode: null,
            });

            await loadMyExams(query);
        }

        return response;
    }, [
        flow,
        examPaper,
        examQuestions,
        saveAnswersBulk,
        autoGradeMcqPaper,
        loadMyExams,
        query,
    ]);



    const getApiErrorMessage = (response) => {
        return (
            response?.message?.error ||
            response?.message?.message ||
            response?.message ||
            response?.error ||
            ""
        );
    };

    const loadRestartPauseStatus = useCallback(async (assessmentId) => {
        try {
            const response = await CandidateExamService.getRestartPauseStatus(assessmentId);
            const data = response?.data || response?.data?.data || null;

            setRestartPauseStatus(data);
            return data;
        } catch (error) {
            setErrorMessage("Failed to load restart/pause status.");
            return null;
        }
    }, []);

    const startOrResumeExam = useCallback(async (exam, mode = "start") => {
        try {
            setExamLoading(true);
            setErrorMessage("");

            let activeExam = { ...exam };
            const assessmentId = activeExam.assessment_id || activeExam.id;

            await loadRestartPauseStatus(assessmentId);

            // 1. If this is a published exam without candidate_id, prepare/create candidate first
            // if (!activeExam.candidate_id) {
            //     const prepareResponse = await CandidateExamService.preparePublishedExam(
            //         activeExam
            //     );

            //     if (!prepareResponse?.success) {
            //         throw new Error(
            //             prepareResponse?.message?.error ||
            //             prepareResponse?.message ||
            //             "Failed to prepare candidate exam."
            //         );
            //     }

            //     const preparedCandidate =
            //         prepareResponse?.data?.candidate ||
            //         prepareResponse?.data?.data?.candidate;

            //     activeExam = {
            //         ...activeExam,
            //         ...preparedCandidate,
            //         id: assessmentId,
            //         assessment_id: assessmentId,
            //         candidate_id: preparedCandidate.candidate_id,
            //         candidate_status: preparedCandidate.candidate_status,
            //         attempt_no: preparedCandidate.attempt_no,
            //         no_of_attempts: preparedCandidate.no_of_attempts,
            //         name: activeExam.name,
            //         title: activeExam.title,
            //         terms_content: activeExam.terms_content,
            //     };
            // }

            if (!activeExam.candidate_id) {
                throw new Error("Candidate ID is missing. This page only supports enrolled exams.");
            }

            // 2. Validate candidate
            const validationResponse = await testService.validateCandidate(
                activeExam.candidate_id
            );

            const validationData =
                validationResponse?.data?.validation ||
                validationResponse?.data?.data?.validation ||
                null;

            if (validationData) {
                activeExam = {
                    ...activeExam,
                    ...validationData,
                };
            }

            if (!validationResponse?.success) {
                const validationError = getApiErrorMessage(validationResponse);

                if (
                    mode === "resume" &&
                    String(validationError).toLowerCase().includes("time's up")
                ) {
                    const answersResponse = await CandidateExamService.getCandidateAnswers(
                        activeExam.candidate_id
                    );

                    const answers =
                        answersResponse?.data?.answers ||
                        answersResponse?.data?.data?.answers ||
                        [];

                    const paperResponse = await CandidateExamService.loadExamPaper(
                        assessmentId
                    );

                    const payload =
                        paperResponse?.data?.data ||
                        paperResponse?.data ||
                        {};

                    const questions = parseExamQuestions(payload.examQuestions);

                    const restoredAnswers = {};

                    answers.forEach((answer) => {
                        try {
                            restoredAnswers[answer.question_id] = JSON.parse(
                                answer.answer_content
                            );
                        } catch {
                            restoredAnswers[answer.question_id] =
                                answer.answer_content || "";
                        }
                    });

                    await finishExam({
                        answers: restoredAnswers,
                        is_all_mcq: null,
                        examOverride: activeExam,
                        questionsOverride: questions,
                        stayOnPaper: true,
                    });

                    await window.Swal.fire({
                        title: "Time is up",
                        text: "Your exam has been submitted automatically.",
                        icon: "warning",
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        confirmButtonText: "OK",
                    });

                    const feedbackExam = {
                        ...activeExam,
                        assessment_details: payload.assessment_details || {},
                        feedbackSummary: {
                            assessment_title:
                                payload.assessment_details?.title ||
                                activeExam.name ||
                                activeExam.title ||
                                "",
                            subject:
                                payload.assessment_details?.subject ||
                                activeExam.subject ||
                                "",
                            time_duration:
                                payload.assessment_details?.time_duration ||
                                activeExam.exam_time_interval ||
                                null,
                            questionCount: questions.length,
                            answeredCount: Object.keys(restoredAnswers).length,
                            examStartTime:
                                getExamStartTimeFromData(validationData, activeExam),
                            examFinishTime: new Date().toISOString(),
                        },
                    };

                    setFlow({
                        view: "feedback",
                        selectedExam: feedbackExam,
                        mode: null,
                    });

                    return true;
                }

                throw new Error(validationError || "Candidate validation failed.");
            }

            // 3. Start timer
            let timerData = null;

            if (mode === "start") {
                const timerResponse = await CandidateExamService.startTimer({
                    candidate_id: activeExam.candidate_id,
                    assessment_id: assessmentId,
                    attempt_no: activeExam.attempt_no,
                });

                timerData = timerResponse?.data?.data || timerResponse?.data || null;
            } else {
                const validationTimerData =
                    validationResponse?.data?.validation ||
                    validationResponse?.data?.data?.validation ||
                    activeExam;

                const reportResponse = await CandidateExamService.getReport({
                    candidate_id: activeExam.candidate_id,
                    attempt_no: activeExam.attempt_no || 1,
                });

                const reportData = reportResponse?.data?.assessment;

                const attemptStartTime = reportData?.attempt_start_time;

                timerData = {
                    ...validationTimerData,
                    ...(attemptStartTime
                        ? { attempt_start_time: attemptStartTime }
                        : {}),
                };

                if (attemptStartTime) {
                    activeExam = {
                        ...activeExam,
                        attempt_start_time: attemptStartTime,
                    };
                }
            }
            // const timerResponse = await CandidateExamService.startTimer({
            //     candidate_id: activeExam.candidate_id,
            //     assessment_id: assessmentId,
            //     attempt_no: activeExam.attempt_no,
            // });

            // const timerData = timerResponse?.data?.data || timerResponse?.data || null;

            // 4. Load questions
            const paperResponse = await CandidateExamService.loadExamPaper(assessmentId);

            let answers = [];

            // 5. Resume answers only for resume mode
            if (mode === "resume") {
                const answersResponse = await CandidateExamService.getCandidateAnswers(
                    activeExam.candidate_id
                );

                answers =
                    answersResponse?.data?.answers ||
                    answersResponse?.data?.data?.answers ||
                    [];
            }

            const payload = paperResponse?.data?.data || paperResponse?.data || {};
            const questions = parseExamQuestions(payload.examQuestions);
            // if (mode === "resume" && hasExamTimeExpired({ exam: activeExam, timerData })) {
            //     console.log("Resume expiry check:", {
            //         mode,
            //         activeExam,
            //         timerData,
            //         expired: hasExamTimeExpired({ exam: activeExam, timerData }),
            //     });
            //     const restoredAnswers = {};

            //     answers.forEach((answer) => {
            //         try {
            //             restoredAnswers[answer.question_id] = JSON.parse(answer.answer_content);
            //         } catch {
            //             restoredAnswers[answer.question_id] = answer.answer_content || "";
            //         }
            //     });

            //     await finishExam({
            //         answers: restoredAnswers,
            //         is_all_mcq: null,
            //         examOverride: activeExam,
            //         questionsOverride: questions,
            //     });
            //     setErrorMessage(
            //         "Your exam time had already ended, so the exam was submitted automatically."
            //     );

            //     setFlow({
            //         view: "list",
            //         selectedExam: null,
            //         mode: null,
            //     });

            //     await loadPublishedExams(query);

            //     return false;
            // }

            // setExamStartData(timerResponse?.data || timerResponse?.data?.data || null);
            // setCandidateAnswers(answers);

            // setExamPaper({
            //     ...activeExam,
            //     assessment_details: payload.assessment_details || {},
            // });

            const finalExamStartData = timerData;

            const finalExamPaper = {
                ...activeExam,
                assessment_details: payload.assessment_details || {},
            };

            setExamStartData(finalExamStartData);
            setCandidateAnswers(answers);

            setExamPaper(finalExamPaper);

            setExamQuestions(questions);
            setExamSections(payload.sections || []);

            setFlow({
                view: "paper",
                selectedExam: activeExam,
                mode,
            });

            return true;
        } catch (error) {
            setErrorMessage(error?.message || "Failed to start exam.");
            return false;
        } finally {
            setExamLoading(false);
        }
    }, [loadRestartPauseStatus, finishExam, loadMyExams, query]);

    const loadMetaData = useCallback(async (isMounted = { current: true }) => {
        try {
            setMetaLoading(true);

            const response = await CandidateExamService.getExamMetaData();

            const metaDataArray = Array.isArray(response?.data)
                ? response.data
                : Array.isArray(response?.data?.data)
                    ? response.data.data
                    : [];

            if (!isMounted.current) return;

            setMetaData(normalizeMetaData(metaDataArray));
        } catch (error) {
            if (!isMounted.current) return;
            setErrorMessage(error?.message || "Failed to load filters.");
        } finally {
            if (isMounted.current) setMetaLoading(false);
        }
    }, []);

    const updateQuery = useCallback((patch) => {
        setHasLoadedExams(false);

        setQuery((prev) => ({
            ...prev,
            ...patch,
            offset: patch.offset !== undefined ? patch.offset : 0,
        }));
    }, []);

    const clearQuery = useCallback(() => {
        setHasLoadedExams(false);
        setQuery(defaultQuery);
    }, []);

    const loadExamPaper = useCallback(async (exam) => {
        try {
            setExamLoading(true);
            setErrorMessage("");

            const response = await CandidateExamService.loadExamPaper(exam.id);

            const payload = response?.data || response?.data?.data || {};
            const questions = parseExamQuestions(payload.examQuestions);

            setExamPaper({
                ...exam,
                assessment_details: payload.assessment_details || {},
            });

            setExamQuestions(questions);
            setExamSections(payload.sections || []);

            setFlow({
                view: "paper",
                selectedExam: exam,
                mode: flow.mode || "start",
            });
        } catch (error) {
            setErrorMessage(error?.message || "Failed to load exam paper.");
        } finally {
            setExamLoading(false);
        }
    }, [flow.mode]);

    const openExamConsent = useCallback((exam, mode = "start") => {
        setFlow({
            view: "consent",
            selectedExam: exam,
            mode,
        });
    }, []);

    const openGradeReport = useCallback((exam) => {
        setFlow({
            view: "grade-report",
            selectedExam: exam,
            mode: null,
        });
    }, []);

    const tryAgainExam = useCallback(async (exam) => {
        try {
            setExamLoading(true);
            setErrorMessage("");

            const loginUserId = sessionStorage.getItem("user_id");
            const assessmentId = exam.assessment_id || exam.id;

            const lastAttempt = await AssessmentService.getAllLastAttemp(
                loginUserId,
                assessmentId
            );

            if (lastAttempt.data.attempt_no >= lastAttempt.data.no_of_attempts) {
                throw new Error("All the attempts are over for this exam");
            }

            const currentTime = new Date().toISOString().slice(0, 19);

            const newAttempt = [
                {
                    added_by: lastAttempt.data.added_by,
                    assessment_id: lastAttempt.data.assessment_id,
                    attempt_no: lastAttempt.data.attempt_no + 1,
                    candidate_id: lastAttempt.data.candidate_id,
                    candidate_status: "Assigned",
                    no_of_attempts: lastAttempt.data.no_of_attempts,
                    node_mid: lastAttempt.data.node_mid,
                    parent_user_id: lastAttempt.data.parent_user_id,
                    user_id: lastAttempt.data.user_id,
                    student_id: lastAttempt.data.student_id,
                    start_time: currentTime,
                    exam_time_interval: lastAttempt.data.exam_time_interval,
                    buffer_time: lastAttempt.data.buffer_time,
                    exam_time_interval_unit_id: lastAttempt.data.exam_time_interval_unit_id,
                    buffer_time_unit_id: lastAttempt.data.buffer_time_unit_id,
                    is_set_time_limit: lastAttempt.data.is_set_time_limit,
                },
            ];

            const response = await AssessmentService.AddCandidate(
                JSON.stringify(newAttempt)
            );

            if (!response.success) {
                throw new Error(response.message || "Failed to add new attempt.");
            }

            const acknowledged = await sweetSuccessAlert(
                "Success",
                "New attempt added.",
                "success",
                "OK"
            );

            if (!acknowledged) {
                return;
            }

            history.replace({
                pathname: routes.exm,
                state: {
                    examView: "new-exams",
                },
            });

            setExamViewMode("new-exams");
            setFlow({
                view: "list",
                selectedExam: null,
                mode: null,
            });
        } catch (error) {
            setErrorMessage(error?.message || "Failed to add new attempt.");
        } finally {
            setExamLoading(false);
        }
    }, [loadMyExams, query]);

    const restartCurrentExam = useCallback(async (exam) => {
        try {
            setExamLoading(true);
            setErrorMessage("");

            if (!exam?.candidate_id) {
                throw new Error("Missing candidate ID. Cannot restart exam.");
            }

            const response = await CandidateExamService.restartExam(exam.candidate_id);

            if (!response?.success) {
                throw new Error(
                    response?.message?.error ||
                    response?.message ||
                    "Failed to restart exam."
                );
            }

            const assessmentId = exam.assessment_id || exam.id;

            const timerResponse = await CandidateExamService.startTimer({
                candidate_id: exam.candidate_id,
                assessment_id: assessmentId,
                attempt_no: exam.attempt_no,
            });

            const timerData =
                timerResponse?.data?.data ||
                timerResponse?.data ||
                null;

            const restartData = response?.data?.data || response?.data || {};

            const newStartTime =
                timerData?.attempt_start_time ||
                timerData?.start_time ||
                restartData?.start_time;

            const restartedExam = {
                ...exam,
                start_time: newStartTime,
                attempt_start_time: newStartTime,
                candidate_status: "Onprogress",
            };


            setCandidateAnswers([]);
            setExamStartData({
                ...timerData,
                start_time: newStartTime,
            });

            setExamPaper((current) => ({
                ...current,
                ...restartedExam,
            }));


            setFlow({
                view: "paper",
                selectedExam: restartedExam,
                mode: "start",
            });

            // await loadMyExams(query);
            return {
                response,
                timerData: {
                    ...timerData,
                    start_time: newStartTime,
                },
            };
        } catch (error) {
            setErrorMessage(error?.message || "Failed to restart exam.");
            throw error;
        } finally {
            setExamLoading(false);
        }
    }, []);

    const resetRestartPauseStatus = useCallback(() => {
        setRestartPauseStatus((current) => ({
            ...current,
            is_pause: false,
            pause_until: null,
            pause_start_time: null,
            pause_end_time: null,
        }));
    }, []);

    const backToExamList = useCallback(async () => {
        setFlow({
            view: "list",
            selectedExam: null,
            mode: null,
        });

        setHasLoadedExams(false);
        await loadMyExams(query);
    }, [loadMyExams, query]);

    console.log("Current exam flow view:", flow.view);

    useEffect(() => {
        //const isExamMode = isExamModeActive(window.location.pathname, flow.view);
        const isExamMode = flow?.view === "consent" || flow?.view === "paper";
        console.log("body exam mode:", isExamMode, flow?.view);
        document.body.classList.toggle("exam-mode-active", isExamMode);

        return () => {
            document.body.classList.remove("exam-mode-active");
        };
    }, [flow?.view]);

    useEffect(() => {
        const isMounted = { current: true };

        if (!hasLoadedExams) {
            loadMyExams(query, isMounted);
        }

        return () => {
            isMounted.current = false;
        };
    }, [hasLoadedExams, loadMyExams, query]);

    useEffect(() => {
        const isMounted = { current: true };
        loadMetaData(isMounted);
        return () => {
            isMounted.current = false;
        };
    }, [loadMetaData]);

    return (
        <ExamContext.Provider
            value={{
                items,
                allAttemptItems,
                query,
                totalCount,
                loading,
                errorMessage,
                flow,
                updateQuery,
                clearQuery,
                loadMyExams,
                openExamConsent,
                openGradeReport,
                backToExamList,
                metaData,
                metaLoading,
                loadMetaData,
                examPaper,
                examQuestions,
                examSections,
                examLoading,
                loadExamPaper,
                candidateAnswers,
                examStartData,
                startOrResumeExam,
                saveAnswer,
                saveAnswersBulk,
                finishExam,
                tryAgainExam,
                restartPauseStatus,
                loadRestartPauseStatus,
                restartCurrentExam,
                resetRestartPauseStatus,
                examViewMode,
                setExamViewMode,

            }}
        >
            {children}
        </ExamContext.Provider>
    );
}

export function useExamContext() {
    const context = useContext(ExamContext);

    if (!context) {
        throw new Error("useExamContext must be used inside ExamProvider");
    }

    return context;
}
